<script lang="ts">
  import type { PwNextSocialProofProps } from '../types';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import { Text, CarouselButton } from '@squareup/public-web-design-system';
  import PwNextNoise from '../../PwNextPartials/PwNextNoise/PwNextNoise.svelte';

  export let cta: PwNextSocialProofProps['cta'];
  export let noise: PwNextSocialProofProps['noise'];
  export let type: PwNextSocialProofProps['type'];
  export let slides: PwNextSocialProofProps['slides'];
  export let fallbackBgColor: PwNextSocialProofProps['fallbackBgColor'];
  export let isBalanced: PwNextSocialProofProps['isBalanced'];

  let currentSlide = 0;
  let hasNavigated = false;
  let direction: 'next' | 'prev' = 'next';

  const trackSlideChange = () => {
    const slideQuote = slides?.[currentSlide]?.quote;

    window.Sq.Tracking.track(
      {
        category: 'Social Proof',
        action: 'Slide View',
        properties: {
          index: currentSlide,
          quote: slideQuote,
        },
      },
      { destinations: ['googleAnalytics'] }
    );

    window.Sq.Tracking.track(
      {
        action: 'Advance Social Proof Slide',
        properties: {
          slideIndex: currentSlide,
          slideQuote,
        },
      },
      { destinations: ['cdp'] }
    );
  };

  const nextSlide = () => {
    hasNavigated = true;
    direction = 'next';

    if (slides?.length) {
      // Check if slides and its length are defined
      currentSlide++;
      if (currentSlide >= slides.length) {
        currentSlide = 0;
      }
      trackSlideChange();
    } else {
      console.error('Slides array is undefined or empty');
    }
  };

  const prevSlide = () => {
    hasNavigated = true;
    direction = 'prev';

    if (slides?.length) {
      // Check if slides and its length are defined
      currentSlide--;
      if (currentSlide < 0) {
        currentSlide = slides.length - 1;
      }
      trackSlideChange();
    } else {
      console.error('Slides array is undefined or empty');
    }
  };

  $: slideData = slides?.[currentSlide];
</script>

<div
  class="SocialProofSlide SocialProofSlide--{type}"
  class:logoPrimary={type !== 'hero' && !slideData?.image}
  class:loading={!hasNavigated}
  class:animateSlidesReverse={direction === 'prev'}
  class:single={(slides?.length ?? 0) <= 1}
>
  <div
    class="slideBackground-container onLoad"
    style:--fallback-bg-color={fallbackBgColor}
    style:--maxZindex={(slides?.length ?? 0) + 1}
  >
    {#if slides?.length}
      {#each slides as slide, id}
        {#if slide.slideBackground}
          <div
            class="{slide.slideBackground?.props.key} slideBackground"
            class:active={currentSlide === id}
            style:--order={direction === 'next' ? id : slides.length - id}
          >
            <DynamicChildrenComponent children={[slide.slideBackground]} />
            {#if noise.useOverlay}
              <PwNextNoise
                noiseImage={noise.noiseImage}
                opacity={noise.noiseSettings?.[`${id + 1}`]?.opacity}
                size={noise.noiseSettings?.[`${id + 1}`]?.size}
              />
            {/if}
          </div>
        {/if}
      {/each}
    {/if}
  </div>

  <div class="SocialProofSlide-content">
    {#if slides?.length}
      {#each slides as slide, id}
        {#if slide.image}
          <div class="image" class:active={currentSlide === id}>
            <DynamicChildrenComponent children={[slide.image]} />
          </div>
        {/if}

        <blockquote
          class="SocialProofSlide-quote theme-{slide.theme}"
          class:active={currentSlide === id}
          class:balance-text={isBalanced}
        >
          <Text variant="600" as="span">{slide.quote}</Text>
        </blockquote>

        <div class="SocialProofSlide-attribution theme-{slide.theme}" class:active={currentSlide === id}>
          {#if slide.logo}
            <div class="logo">
              <DynamicChildrenComponent children={[slide.logo]} />
            </div>
          {/if}
          <cite class="attribution-text">
            <Text variant="100">{slide.name}</Text>
            <div>
              <Text variant="100">{slide.business}</Text>
              <Text variant="100">{slide.location}</Text>
              {#if slide.description}
                <Text variant="100">{slide.description}</Text>
              {/if}
            </div>
          </cite>
        </div>
      {/each}

      <div class="SocialProofSlide-arrows theme-{slideData?.theme}">
        {#if slides?.length > 1}
          <CarouselButton
            direction="previous"
            onClick={prevSlide}
            colorMode={slides[currentSlide].theme === 'white' ? 'dark' : 'light'}
          />
          <CarouselButton
            direction="next"
            onClick={nextSlide}
            colorMode={slides[currentSlide].theme === 'white' ? 'dark' : 'light'}
          />
        {/if}
        {#if cta || slideData?.cta}
          <div class="SocialProofSlide-link">
            <DynamicChildrenComponent
              children={[slideData?.cta ?? cta]}
              passThroughProps={{
                size: type === 'hero' ? 'large' : 'regular',
              }}
            />
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as mediaQueries;
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/variables.scss' as vars;

  .SocialProofSlide {
    width: 100%;

    // #region default styles
    &-content {
      position: relative;
      z-index: 5;
      height: 100%;
      display: grid;
      gap: var(--space-40) var(--grid-container-gap);
      grid-template-columns: repeat(2, 1fr);

      @include mediaQueries.at('medium') {
        grid-template-columns: repeat(var(--grid-container-columns), 1fr);
      }
    }

    &-arrows {
      z-index: 1;
      position: relative;
      display: flex;
      flex-wrap: wrap;
    }

    :global {
      .SocialProofSlide-quote {
        & > span {
          color: var(--color, var(--color-text)); //this to be removed if updated in design system
        }
      }

      .backgroundImage,
      .backgroundImage img,
      .gradient,
      .gradient img {
        position: absolute;
        object-fit: cover;
        width: 100%;
        height: 100%;
        left: 0;

        .ResponsiveImagePartial {
          height: 100%;
          width: 100%;
        }
        .AproposImageComponent {
          height: 100%;
        }
      }

      .logo {
        width: var(--space-80);
        flex-shrink: 0;
        @include mediaQueries.at('small') {
          width: var(--space-100);
        }
      }
      .logo img {
        max-width: 40rem;
      }
    }

    &-attribution {
      display: flex;
      gap: var(--space-20);
      .attribution-text {
        display: flex;
        flex-direction: column;
        gap: var(--space-20);
      }

      @include mediaQueries.at('small') {
        gap: var(--space-40);
      }
    }

    &-arrows {
      z-index: 1;
      position: relative;
      display: flex;
      gap: var(--space-20);
      align-items: center;
    }

    .slideBackground-container {
      background-color: var(--fallback-bg-color);
      &.onLoad {
        width: var(--container-width-current);
        border-radius: var(--container-border-radius);
      }
    }

    // #endregion

    // #region hero styles
    &--hero {
      .SocialProofSlide-content {
        justify-content: center;
        padding: var(--space-100) var(--space-20);
        padding-top: 0;
        gap: var(--space-40);
        background-color: black;

        align-content: center;
        grid-template-rows: auto;

        @include mediaQueries.at('medium') {
          background-color: transparent;

          padding: var(--space-100) 0;
          margin-top: 0;
          min-height: 120vh;
        }
      }

      .slideBackground-container {
        display: grid;
        position: relative;
        min-width: 100%;

        //adding background gradient fading into image on mobile
        &::before {
          z-index: calc(var(--maxZindex, 20) + 1);
          background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%);
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: var(--space-180);
        }

        @include mediaQueries.at('medium') {
          position: absolute;
          overflow: hidden;
          height: 100%;
          z-index: 0;
          left: 50%;
          transform: translate3d(-50%, 0, 0); //need to add translateZ to remove artifacts when animating
          min-width: var(--container-width-current);

          &:before {
            background: linear-gradient(90deg, #000 0%, rgba(0, 0, 0, 0.5) 77.5%, rgba(0, 0, 0, 0) 100%);
            z-index: calc(var(--maxZindex, 20) + 1);
            content: '';
            position: absolute;
            left: 0;
            width: 50%;
            height: 100%;
          }
        }
      }

      .backgroundImage {
        grid-area: image;
        position: relative;
        :global {
          img {
            position: relative;
          }
        }
        @include mediaQueries.at('medium') {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 100vw;
        }
        @media (min-width: vars.$body-width-max) {
          width: var(--body-width-max);
        }
      }

      .SocialProofSlide-quote {
        grid-column: 1 / span 2;
        grid-row: 2;
      }
      .SocialProofSlide-attribution {
        grid-row: 1;
        grid-column: 1 / span 2;
        gap: var(--space-40);
      }

      .SocialProofSlide-arrows {
        grid-column: 1 / span 2;
        grid-row: 3;
      }

      @include mediaQueries.at('medium') {
        .SocialProofSlide-attribution,
        .SocialProofSlide-quote,
        .SocialProofSlide-arrows {
          grid-column: 1 / span 6;
        }
      }
    }
    // #endregion

    // #region base & card styles
    &--base,
    &--card {
      .SocialProofSlide-content {
        max-width: var(--container-width-current);
        margin: auto;
        padding: var(--space-60) var(--space-20);

        @include mediaQueries.at('medium') {
          padding: var(--space-100) var(--space-60);
        }
      }

      .SocialProofSlide-quote {
        grid-column: 1 / span 2;
        grid-row: 2;

        @include mediaQueries.at('medium') {
          grid-column: 6 / span 7;
          grid-row: 1;
        }
      }
      .image {
        grid-column: 1 / span 2;
        grid-row: 1;

        @include mediaQueries.at('medium') {
          grid-column: 1 / span 3;
          grid-row: 1;
        }

        &.no-bg-slide {
          @include mediaQueries.at('medium') {
            grid-column: 1 / span 4;
            grid-row: 1;
          }
        }
      }

      .SocialProofSlide-arrows {
        grid-column: 1 / span 2;
        grid-row: 4;

        @include mediaQueries.at('medium') {
          grid-column: 1 / span 3;
          grid-row: 2;
          margin-top: auto;
        }
      }

      .SocialProofSlide-attribution {
        grid-column: 1 / span 2;
        grid-row: 3;

        @include mediaQueries.at('medium') {
          grid-column: 6 / span 7;
          grid-row: 2;
        }
      }

      .slideBackground-container {
        position: absolute;
        overflow: hidden;
        height: 100%;
        z-index: 0;
        left: 50%;
        transform: translateX(-50%) translateZ(0px); //need to add translateZ to remove artifacts when animating
        min-width: var(--container-width-current);
      }

      .slideBackground {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        width: 100%;

        @include mediaQueries.at('extra-small') {
          width: 100vw;
        }
        @media (min-width: vars.$body-width-max) {
          width: var(--body-width-max);
        }
      }
    }
    // #endregion

    //#region base styles
    &--base {
      .SocialProofSlide-content {
        padding: 0;
      }
    }
    // #endregion

    // #region single layout
    &--base,
    &--card {
      &.single {
        @include mediaQueries.at('medium') {
          .image {
            grid-row: 1 / span 2;
          }

          .SocialProofSlide-quote {
            display: flex;
            align-items: flex-end;
          }
        }

        .SocialProofSlide-arrows {
          grid-column: 1 / span 2;
          grid-row: 4;

          @include mediaQueries.at('small') {
            grid-column: 6 / span 7;
            grid-row: 3;
          }

          @include mediaQueries.at('medium') {
            grid-column: 1 / span 4;
          }
        }

        .SocialProofSlide-content {
          @include mediaQueries.at('small') {
            grid-template-columns: repeat(var(--grid-container-columns), 1fr);
            .image {
              grid-column: 1 / span 4;
              grid-row: 1 / span 2;
            }

            .SocialProofSlide-quote {
              grid-column: 6 / span 7;
              grid-row: 1;
            }

            .SocialProofSlide-attribution {
              grid-column: 6 / span 7;
              grid-row: 2;
            }
          }

          @include mediaQueries.at('large') {
            .image {
              grid-area: 1 / span 4 / span 2 / 5;
            }
          }
        }
      }
    }
    // #endregion

    //#region base logo only
    &.logoPrimary {
      .SocialProofSlide-content {
        .SocialProofSlide-attribution {
          grid-column: 1 / span 3;
          grid-row: 1;
          flex-direction: column;
        }
      }
    } // #endregion

    //#region css animations
    @keyframes animate-in {
      0% {
        opacity: 0;
        visibility: hidden;
      }
      100% {
        opacity: 1;
        visibility: visible;
      }
    }

    @keyframes animate-out {
      0% {
        visibility: visible;
        z-index: calc(var(--maxZindex));
      }
      100% {
        visibility: hidden;
        z-index: var(--order);
      }
    }

    @keyframes animate-in-content {
      0% {
        visibility: hidden;
        transform: translateX(var(--direction-in));
      }
      100% {
        visibility: visible;
        transform: translateX(0px);
      }
    }

    @keyframes animate-out-content {
      0% {
        visibility: visible;
      }
      100% {
        visibility: hidden;
        transform: translateX(var(--direction-out));
      }
    }

    --direction-in: 20px;
    --direction-out: -20px;
    --animation-duration: 0.6s;
    .slideBackground {
      animation: animate-out var(--animation-duration) 0s var(--easing-standard) forwards;
      z-index: var(--order, 0);
      &.active {
        visibility: visible;

        z-index: var(--maxZindex, 100);

        animation: animate-in var(--animation-duration) var(--easing-standard) forwards;
      }
    }

    .SocialProofSlide-attribution,
    .image,
    .SocialProofSlide-quote {
      animation: animate-out-content var(--animation-duration) var(--easing-standard) forwards;
      transition: opacity var(--animation-duration) var(--easing-standard);
      opacity: 0;

      &.active {
        animation: animate-in-content var(--animation-duration) var(--easing-standard) forwards;
        opacity: 1;
      }
    }

    &.animateSlidesReverse {
      --direction-in: -20px;
      --direction-out: 20px;
    }

    &.loading {
      .SocialProofSlide-attribution,
      .image,
      .SocialProofSlide-quote {
        --direction-in: 0px;
        &:not(.active) {
          opacity: 0;
        }
      }
    }

    // #endregion
  }
</style>
